import { NavLink, Outlet } from "react-router";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../../shell/VenueContext";
import { useVenueEntitlements } from "../../shell/entitlements";
import { SETTINGS_SECTIONS } from "../../shell/nav";
import { canAccessVenue } from "../../shell/roles";
import { ShellIcon } from "../../shell/icons";

/**
 * The venue Settings hub. One rail entry ("Paramètres") opens this page; every venue-configuration
 * screen (general, floor plan, media, public site, booking channels, anti-abuse, onboarding, staff)
 * lives here under a shared sub-navigation + `<Outlet/>`, instead of flooding the primary left rail.
 *
 * The sub-nav is filtered through the SAME `canAccessVenue` + entitlement checks the rail and route
 * guards use, so a link the user cannot open is never shown. `disabled` items (a destination that
 * isn't built yet) render as non-interactive rows with a "bientôt" badge.
 */
export function SettingsLayout() {
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { has } = useVenueEntitlements();
  const { t } = useTranslation();

  // Role hides; a missing entitlement shows the item disabled-with-a-lock (discoverable), not hidden.
  const items = SETTINGS_SECTIONS.filter((entry) => canAccessVenue(claims, venueId, entry.minRole));

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "6",
        p: { base: "4", md: "6" },
      })}
    >
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "fg.default" })}>
        {t("shell.nav.settings")}
      </h1>
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", md: "14rem 1fr" },
          gap: { base: "4", md: "8" },
          alignItems: "start",
        })}
      >
        <nav
          aria-label={t("shell.settings.navLabel")}
          className={css({ display: "flex", flexDirection: "column", gap: "1" })}
        >
          {items.map((entry) => {
            const label = t(entry.labelKey);
            const locked = typeof entry.feature === "string" && !has(entry.feature);
            const inner = (
              <>
                <span className={css({ fontSize: "md", display: "inline-flex", flexShrink: 0 })}>
                  <ShellIcon name={entry.icon} />
                </span>
                <span className={css({ truncate: true, flex: "1" })}>{label}</span>
                {entry.disabled && (
                  <span
                    className={css({
                      fontSize: "2xs",
                      fontWeight: "medium",
                      color: "fg.subtle",
                      bg: "bg.muted",
                      rounded: "sm",
                      px: "1.5",
                      py: "0.5",
                    })}
                  >
                    {t("shell.settings.soon")}
                  </span>
                )}
                {locked && !entry.disabled && (
                  <span
                    aria-label={t("nav.locked")}
                    className={css({ fontSize: "sm", color: "fg.subtle", flexShrink: 0 })}
                  >
                    <ShellIcon name="lock" />
                  </span>
                )}
              </>
            );
            const base = {
              display: "flex",
              alignItems: "center",
              gap: "2.5",
              px: "3",
              py: "2",
              rounded: "md",
              fontSize: "sm",
            } as const;
            if (entry.disabled) {
              return (
                <span
                  key={entry.to}
                  aria-disabled="true"
                  className={css({ ...base, color: "fg.subtle", cursor: "not-allowed" })}
                >
                  {inner}
                </span>
              );
            }
            return (
              <NavLink
                key={entry.to}
                to={`/v/${venueId}/${entry.to}`}
                title={locked ? t("nav.locked") : undefined}
                className={({ isActive }: { isActive: boolean }) => {
                  const active = isActive && !locked;
                  return css({
                    ...base,
                    fontWeight: active ? "semibold" : "medium",
                    bg: active ? "colorPalette.subtle" : "transparent",
                    color: locked ? "fg.subtle" : active ? "colorPalette.text" : "fg.muted",
                    _hover: { bg: "bg.muted", color: locked ? "fg.muted" : "fg.default" },
                  });
                }}
              >
                {inner}
              </NavLink>
            );
          })}
        </nav>
        <div className={css({ minW: "0" })}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
